import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, FileText, AlertTriangle } from 'lucide-react';

const IncidentReportList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/incident-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load incident reports');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'Low': 'bg-green-100 text-green-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-orange-100 text-orange-800',
      'Critical': 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p>Loading incident reports...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <AlertTriangle className="h-6 w-6 text-orange-600" />
          <h2 className="text-2xl font-bold">Incident Reports</h2>
        </div>
        <Button onClick={() => navigate('/field/incident-report/new')} data-testid="new-incident-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Report
        </Button>
      </div>

      {reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 mb-4">No incident reports found</p>
            <Button onClick={() => navigate('/field/incident-report/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <Card key={report.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/field/incident-report/${report.id}`)}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{report.incident_type}</CardTitle>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                      <span><strong>Location:</strong> {report.location}</span>
                      <span>•</span>
                      <span><strong>Date:</strong> {_fmtDate(report.incident_date)}</span>
                    </div>
                  </div>
                  <Badge className={getSeverityColor(report.severity)}>
                    {report.severity}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-600">
                  <p className="mb-2">{report.description?.substring(0, 150)}{report.description?.length > 150 ? '...' : ''}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span><strong>Reported by:</strong> {report.reporter_name}</span>
                    {report.witnesses && report.witnesses.length > 0 && (
                      <span><strong>Witnesses:</strong> {report.witnesses.length}</span>
                    )}
                    {report.photos && report.photos.length > 0 && (
                      <span><strong>Photos:</strong> {report.photos.length}</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default IncidentReportList;
